import React, { useState, useEffect } from "react";
import { ChatIcon, SearchIcon } from "@heroicons/react/solid";
import { RiChatNewFill } from "react-icons/ri";
import ChatList from "./ChatList";
import NeedsAttentionList from "./NeedsAttentionList";
import useGetChatList from "../../hooks/chats/useGetChatList";
import PrimaryLoader from "../../components/common/PrimaryLoader";
import { useSocket } from "../../context/SocketContext";
import { Button } from "../../components/ui";

const ChatSidebar = ({
  selectedChat,
  handleOpenModal,
  handleChatSelect,
  needsAttentionRefreshTrigger,
  chatListRefreshTrigger,
  refreshNeedsAttention,
}) => {
  const [activeTab, setActiveTab] = useState("all");
  const [searchText, setSearchText] = useState("");
  const [initialLoading, setInitialLoading] = useState(true);

  // Get socket context for listening to events
  const { isConnected, on, removeListener } = useSocket();

  // Get the chat list from the API using the hook
  const {
    chatList: apiChatList,
    loading: apiLoading,
    refetch,
  } = useGetChatList();
  const chatList = apiChatList;

  // Listen for the refresh trigger to refetch data
  useEffect(() => {
    if (chatListRefreshTrigger > 0) {
      // console.log("Refreshing chat list from API");
      refetch();
    }
  }, [chatListRefreshTrigger, refetch]);

  // Listen for socket events to refresh the chat list
  useEffect(() => {
    if (isConnected) {
      // Use a specific event name for this component to avoid duplicates
      const chatListEventHandler = (response) => {
        // console.log("Chat sidebar received chat-list update:", response);
        // Refresh the chat list when we get a chat-list event
        refetch();

        // Check if this is a needs attention message and refresh the needs attention list
        if (
          response &&
          response.chat &&
          response.chat.is_needs_attention === 1
        ) {
          // console.log("Chat-list update contains needs attention message, refreshing needs attention list");
          refreshNeedsAttention();
        }
      };

      // Add the listener with a specific name
      on("chat-list", chatListEventHandler);

      // Cleanup function - remove this specific listener when component unmounts
      return () => {
        if (isConnected) {
          removeListener("chat-list", chatListEventHandler);
        }
      };
    }
  }, [isConnected, refetch, on, removeListener, refreshNeedsAttention]);

  // Set initial loading state
  useEffect(() => {
    if (!apiLoading && apiChatList) {
      setInitialLoading(false);
    }
  }, [apiChatList, apiLoading]);

  // Filter chats based on search text
  const filteredChats =
    searchText && chatList
      ? chatList.filter(
          (chat) =>
            (chat.other_member?.first_name || "")
              .toLowerCase()
              .includes(searchText.toLowerCase()) ||
            (chat.other_member?.last_name || "")
              .toLowerCase()
              .includes(searchText.toLowerCase())
        )
      : chatList;

  // Check if we have any chats
  const showEmptyState =
    !initialLoading && (!filteredChats || filteredChats.length === 0);

  // Count chats that need attention
  const needsAttentionCount = filteredChats
    ? filteredChats.filter(
        (chat) => chat.chat && chat.chat.is_needs_attention === 1
      ).length
    : 0;

  return (
    <div className="w-[350px] border-r border-gray-200 flex flex-col">
      {/* Sidebar Header */}
      <div className="p-4 bg-gray-100 flex items-center justify-between">
        <h2 className="text-xl font-semibold">Chats</h2>
        <Button
          className="rounded-full hover:bg-gray-200"
          onClick={handleOpenModal}
          variant={"ghost"}
          size={"icon"}
        >
          <RiChatNewFill className="w-6 h-6" />
        </Button>
      </div>

      {/* Search Chats */}
      <div className="p-3 border-b border-gray-200">
        <div className="bg-gray-100 flex items-center rounded px-2">
          <div className="p-2" aria-label="search">
            <SearchIcon className="w-5 h-5 text-gray-500" />
          </div>
          <input
            className="ml-2 flex-1 bg-transparent p-2 outline-none"
            placeholder="Search chats"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>
      </div>

      {/* Pill-shaped tabs */}
      <div className="flex p-2 space-x-2 border-b border-gray-200">
        <Button
          onClick={() => setActiveTab("all")}
          className={`rounded-full text-sm font-medium transition-colors ${
            activeTab === "all"
              ? ""
              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
          }`}
        >
          All
        </Button>
        <Button
          onClick={() => setActiveTab("needsAttention")}
          variant={"destructive"}
          className={`rounded-full text-sm font-medium transition-colors ${
            activeTab === "needsAttention"
              ? ""
              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
          }`}
        >
          <span>Needs Attention</span>
          {needsAttentionCount > 0 && (
            <span
              className={`ml-1.5 px-1.5 py-0.5 text-xs rounded-full ${
                activeTab === "needsAttention"
                  ? "bg-white text-red-600"
                  : "bg-red-600 text-white"
              }`}
            >
              {needsAttentionCount}
            </span>
          )}
        </Button>
      </div>

      {/* Chat List or Empty State or Needs Attention */}
      {activeTab === "all" ? (
        initialLoading ? (
          <div className="flex flex-col items-center justify-center p-6 h-full">
            <PrimaryLoader />
          </div>
        ) : showEmptyState ? (
          <div className="flex flex-col items-center justify-center p-6 h-full space-y-4">
            <ChatIcon className="w-10 h-10 text-gray-500" />
            <p className="text-gray-500">No chats yet</p>
            <Button
              className="bg-blue-600 text-white text-sm"
              onClick={handleOpenModal}
            >
              <ChatIcon className="w-4 h-4 mr-2" />
              Start a chat
            </Button>
          </div>
        ) : (
          <ChatList
            chatList={filteredChats}
            selectedChatId={selectedChat?.chat_id}
            handleChatSelect={handleChatSelect}
          />
        )
      ) : (
        <NeedsAttentionList
          selectedChatId={selectedChat?.chat_id}
          handleChatSelect={handleChatSelect}
          refreshTrigger={needsAttentionRefreshTrigger}
        />
      )}
    </div>
  );
};

export default ChatSidebar;
